import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CompanyChargeCard from "../components/companyCharges/CompanyChargeCard";
import CreateCompanyChargeModal from "../components/companyCharges/CreateCompanyChargeModal";
import UpdateCompanyChargeModal from "../components/companyCharges/UpdateCompanyChargeModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getCompanyCharges,
  deleteCompanyCharge,
} from "../redux/actions/companyChargeActions";
import { companyChargeActions } from "../redux/slices/companyCharge";
import PageError from "../components/common/PageError";
import { CompanyChargeResponseDto } from "../shared/dtos/companyCharge/CompanyChargeResponseDto";

const CompanyChargesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: companyCharges,
    loadingFetchAll: loadingCompanyCharges,
    errorFetchAll: errorLoadingCompanyCharges,
    serverStatus,
    loadingDelete: loadingCompanyChargeDelete,
    loadingUpdate: loadingCompanyChargeUpdate,
    loadingCreate: loadingCompanyChargeCreate,
    errorCreate: errorCreatingCharge,
    errorUpdate: errorUpdatingCharge,
  } = useAppSelector((state) => state.companyCharge);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedCompanyCharge, setSelectedCompanyCharge] =
    useState<CompanyChargeResponseDto | null>(null);

  const canCreate = hasPermission("company_charge:create");
  const canUpdate = hasPermission("company_charge:update");
  const canDelete = hasPermission("company_charge:delete");

  useEffect(() => {
    dispatch(getCompanyCharges());
  }, [dispatch]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingCompanyChargeCreate) setIsCreateModalOpen(false);
  }, [loadingCompanyChargeCreate]);

  const handleOpenUpdate = (item: CompanyChargeResponseDto): void => {
    setSelectedCompanyCharge(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingCompanyChargeUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedCompanyCharge(null);
    }
  }, [loadingCompanyChargeUpdate]);

  const handleOpenDelete = (item: CompanyChargeResponseDto): void => {
    setSelectedCompanyCharge(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingCompanyChargeDelete) {
      setIsDeleteModalOpen(false);
      setSelectedCompanyCharge(null);
    }
  }, [loadingCompanyChargeDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedCompanyCharge)
      dispatch(deleteCompanyCharge(selectedCompanyCharge.id));
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(companyChargeActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingCompanyCharges) return <AppCircularProgress />;
    if (errorLoadingCompanyCharges) {
      return (
        <PageError
          error={errorLoadingCompanyCharges}
          onRetry={() => dispatch(getCompanyCharges())}
        />
      );
    }

    if (!companyCharges || companyCharges.length === 0) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No company charges found.</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {companyCharges.map((item) => (
          <Grid key={item.id} sx={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CompanyChargeCard
              companyCharge={item}
              onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
              onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Company Charges"
      subtitle="Define specific fees and service charges for your business operations."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Charge
          </AppButton>
        )
      }
    >
      {renderContent()}

      {isCreateModalOpen && (
        <CreateCompanyChargeModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingCompanyChargeCreate}
          error={errorCreatingCharge}
        />
      )}
      {isUpdateModalOpen && selectedCompanyCharge && (
        <UpdateCompanyChargeModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          companyCharge={selectedCompanyCharge}
          loading={loadingCompanyChargeUpdate}
          error={errorUpdatingCharge}
        />
      )}
      {isDeleteModalOpen && selectedCompanyCharge && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Company Charge"
          description={`Are you sure you want to delete "${selectedCompanyCharge.name}"?`}
          loading={loadingCompanyChargeDelete}
        />
      )}
    </PageContainer>
  );
};

export default CompanyChargesPage;
